import React, { useState, useEffect, useRef } from 'react';
import { CreditCard, Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import paymentModeService from '../../services/paymentModeService';
import { paymentManagementService } from '../../firebase/paymentManagement';
import paymentVerificationService from '../../services/paymentVerification';
import toast from 'react-hot-toast';

// Payment Modal Component using Razorpay Checkout (no crypto polyfill needed)
const PaymentModal = ({ 
  isOpen, 
  onClose, 
  paymentData, 
  onSuccess, 
  onFailure,
  title = "Complete Payment"
}) => {
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'processing', 'success', 'failed'
  const [error, setError] = useState(null);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    // Reset state when modal closes
    if (!isOpen) {
      setError(null);
      setPaymentStatus(null);
      setLoading(false);
      hasInitializedRef.current = false; // Reset initialization flag
      return;
    }

    // Only initialize if modal is open, payment data exists, and we haven't already initialized
    if (isOpen && paymentData && !hasInitializedRef.current && paymentStatus !== 'success') {
      // Reset state when modal opens
      setError(null);
      setPaymentStatus(null);
      setLoading(false);
      hasInitializedRef.current = true; // Mark as initialized
      
      // Small delay to ensure modal is rendered before initializing payment
      const timer = setTimeout(() => {
        // Double-check we haven't succeeded before initializing
        if (paymentStatus !== 'success' && hasInitializedRef.current) {
          initializePayment().catch(err => {
            setError(err.message || 'Failed to initialize payment');
            setPaymentStatus('failed');
            setLoading(false);
            hasInitializedRef.current = false; // Allow retry on failure
          });
        }
      }, 300); // Increased delay to ensure modal is fully rendered
      
      return () => clearTimeout(timer);
    } else if (isOpen && !paymentData) {
      setError('Payment data is missing. Please try again.');
      setPaymentStatus('failed');
      setLoading(false);
    }
  }, [isOpen, paymentData]);

  // Handle Razorpay script loading with error suppression
  useEffect(() => {
    const handleRazorpayError = (event) => {
      // Suppress RTB fingerprint header errors from Razorpay
      if (event.message && event.message.includes('x-rtb-fingerprint-id')) {
        event.preventDefault();
        return false;
      }
      
      // Suppress SVG attribute errors from Razorpay's checkout frame
      if (event.message && (
        event.message.includes('<svg> attribute width') ||
        event.message.includes('<svg> attribute height') ||
        event.message.includes('Unexpected end of attribute')
      )) {
        event.preventDefault();
        return false;
      }
      
      // Suppress errors from Razorpay's checkout frame
      if (event.filename && (
        event.filename.includes('checkout-frame.modern.js') ||
        event.filename.includes('checkout-static-next')
      )) {
        event.preventDefault();
        return false;
      }
    };

    const handleUnhandledRejection = (event) => {
      // Suppress unhandled promise rejections from Razorpay
      if (event.reason && (
        event.reason.message?.includes('<svg> attribute') ||
        event.reason.message?.includes('Unexpected end of attribute')
      )) {
        event.preventDefault();
        return false;
      }
    };

    window.addEventListener('error', handleRazorpayError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleRazorpayError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const initializePayment = async () => {
    try {
      const razorpayConfig = paymentModeService.getRazorpayConfig();
      
      if (!razorpayConfig || !razorpayConfig.keyId) {
        setError('Payment system not initialized. Please contact support.');
        setPaymentStatus('failed');
        setLoading(false);
        return;
      }

      // Check if Razorpay is loaded - wait a bit if not loaded yet
      if (!window.Razorpay) {
        // Wait for Razorpay to load (max 3 seconds)
        let attempts = 0;
        const maxAttempts = 30; // 3 seconds with 100ms intervals
        while (!window.Razorpay && attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }
        
        if (!window.Razorpay) {
          setError('Payment system is loading. Please wait a moment and try again.');
          setPaymentStatus('failed');
          setLoading(false);
          return;
        }
      }

      setLoading(true);
      setError(null);
      setPaymentStatus('processing');
      // Validate payment data
      if (!paymentData.amount || paymentData.amount <= 0) {
        throw new Error('Invalid payment amount');
      }

      // Convert amount from rupees to paise (Razorpay expects amount in smallest currency unit)
      // Amount is always in rupees (e.g., 9999 for ₹9,999), so multiply by 100
      const amountInRupees = typeof paymentData.amount === 'number' 
        ? paymentData.amount 
        : parseFloat(paymentData.amount);
      
      if (!isFinite(amountInRupees) || amountInRupees <= 0) {
        throw new Error('Invalid payment amount');
      }

      // Convert to paise (multiply by 100)
      const amountInPaise = Math.round(amountInRupees * 100);
      
      // Validate converted amount
      if (amountInPaise <= 0) {
        throw new Error('Invalid payment amount after conversion');
      }

      // Initialize Razorpay Checkout directly (let Razorpay create the order)
      const options = {
        key: razorpayConfig.keyId,
        amount: amountInPaise, // Amount in paise (e.g., 999900 for ₹9,999)
        currency: paymentData.currency || 'INR',
        name: paymentData.merchantName || 'Mahstry Private Limited',
        description: paymentData.description || 'Payment',
        prefill: {
          name: paymentData.customerName || '',
          email: paymentData.customerEmail || '',
          contact: paymentData.customerPhone || ''
        },
        theme: {
          color: '#3B82F6'
        },
        handler: async function (response) {
          await handlePaymentSuccess(response);
        },
        modal: {
          ondismiss: function() {
            handlePaymentFailure('Payment cancelled by user');
          }
        },
        // Add notes for tracking
        notes: {
          paymentType: paymentData.paymentType || paymentData.type || 'general',
          ...(paymentData.metadata || {})
        }
      };

      const rzp = new window.Razorpay(options);
      
      // Add error handler for Razorpay
      rzp.on('payment.failed', function (response) {
        handlePaymentFailure(response.error?.description || response.error?.reason || 'Payment failed');
      });
      
      rzp.open();

    } catch (error) {
      setError(error.message || 'Failed to initialize payment');
      setPaymentStatus('failed');
      setLoading(false);
      if (onFailure) {
        onFailure(error);
      } else {
        toast.error(error.message || 'Failed to initialize payment');
      }
    }
  };

  const handlePaymentSuccess = async (response) => {
    try {
      setPaymentStatus('processing');
      
      
      // Validate required fields
      if (!response.razorpay_payment_id) {
        throw new Error('Payment ID not received from Razorpay');
      }

      // Verify payment with Razorpay (server-side verification)
      const razorpayConfig = paymentModeService.getRazorpayConfig();
      if (razorpayConfig && razorpayConfig.keySecret) {
        try {
          paymentVerificationService.initialize(
            razorpayConfig.keyId, 
            razorpayConfig.keySecret
          );
          
          // Verify payment signature only if we have order_id and signature
          // When Razorpay auto-creates orders, order_id might be missing
          if (response.razorpay_order_id && response.razorpay_signature) {
            const isValidPayment = paymentVerificationService.verifyPaymentSignature(
              response.razorpay_payment_id,
              response.razorpay_order_id,
              response.razorpay_signature
            );

            if (!isValidPayment) {
              // Don't throw - payment was successful, verification is just a safety check
            }
          }
          // This is okay when Razorpay auto-creates orders

          // Validate payment data (non-blocking)
          try {
            paymentVerificationService.validatePaymentData(paymentData);
          } catch (validationError) {
            // Non-blocking validation error
          }
        } catch (verificationError) {
          // Don't throw - payment was successful, verification is just a safety check
        }
      }
      
      // Helper function to clean undefined values from objects
      const cleanObject = (obj) => {
        if (obj === null || obj === undefined) return null;
        if (typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(cleanObject);
        
        const cleaned = {};
        for (const [key, value] of Object.entries(obj)) {
          if (value !== undefined) {
            cleaned[key] = cleanObject(value);
          }
        }
        return cleaned;
      };

      // Create transaction data
      const transactionData = {
        orderId: response.razorpay_order_id || `order_${Date.now()}`,
        paymentId: response.razorpay_payment_id || `payment_${Date.now()}`,
        signature: response.razorpay_signature || null, // Handle undefined signature
        amount: paymentData.amount,
        currency: paymentData.currency || 'INR',
        status: 'success',
        timestamp: new Date().toISOString(),
        paymentData: cleanObject(paymentData),
        // Add additional required fields for Firebase
        userId: paymentData.userId || null,
        paymentType: paymentData.paymentType || paymentData.type || 'general',
        merchantName: paymentData.merchantName || 'Mahstry Private Limited',
        customerName: paymentData.customerName || null,
        customerEmail: paymentData.customerEmail || null,
        customerPhone: paymentData.customerPhone || null,
        // Include hiring data for teacher hiring
        hiringData: cleanObject(paymentData.hiringData),
        // Include enrollment data for course enrollment
        enrollmentData: cleanObject(paymentData.enrollmentData),
        // Include metadata for coaching subscription and other payment types
        metadata: cleanObject(paymentData.metadata)
      };

      // Save transaction to Firebase
      const saveResult = await paymentManagementService.savePaymentTransaction(transactionData);
      
      if (!saveResult.success) {
        throw new Error(saveResult.error || 'Failed to save transaction');
      }

      // Handle specific payment types (don't let errors block the success callback)
      const paymentType = paymentData.paymentType || paymentData.type;
      try {
        if (paymentType === 'teacher_hiring') {
          await handleTeacherHiringSuccess(transactionData);
        } else if (paymentType === 'course_enrollment') {
          await handleCourseEnrollmentSuccess(transactionData);
        } else if (paymentType === 'coaching_subscription') {
          await handleCoachingSubscriptionSuccess(transactionData);
        }
      } catch (typeError) {
        // Don't block the success callback
        // The component's onSuccess handler will handle subscription updates
      }

      setPaymentStatus('success');
      toast.success('Payment completed successfully!');
      
      // Always call success callback even if type handler had issues
      // The component's handler will update the subscription and close the modal
      if (onSuccess) {
        onSuccess(response, transactionData);
      }

    } catch (error) {
      setError('Payment completed but there was an error processing it. Please contact support.');
      setPaymentStatus('failed');
      // Still try to call onSuccess if payment was successful
      if (onSuccess && response?.razorpay_payment_id) {
        try {
          onSuccess(response, {
            paymentId: response.razorpay_payment_id,
            orderId: response.razorpay_order_id,
            amount: paymentData.amount,
            currency: paymentData.currency || 'INR',
            status: 'success',
            timestamp: new Date().toISOString()
          });
        } catch (callbackError) {
          // Error calling callback
        }
      }
    }
  };

  const handleTeacherHiringSuccess = async (transactionData) => {
    try {
      const hiringData = paymentData.hiringData;
      
      // Save teacher hiring record
      const hiringResult = await paymentManagementService.saveTeacherHiringPayment({
        ...hiringData,
        transactionId: transactionData.paymentId,
        paymentStatus: 'completed',
        paymentAmount: transactionData.amount,
        paymentDate: transactionData.timestamp
      });
      
      if (!hiringResult.success) {
        throw new Error(hiringResult.error || 'Failed to save teacher hiring record');
      }

      toast.success(`Successfully hired ${hiringData.teacherName} for ${hiringData.subject}!`);
    } catch (error) {
      throw error;
    }
  };

  const handleCourseEnrollmentSuccess = async (transactionData) => {
    try {
      const enrollmentData = transactionData.enrollmentData;
      
      if (!enrollmentData) {
        throw new Error('Enrollment data not found in transaction');
      }
      
      // Clean enrollment data to remove undefined values
      const cleanEnrollmentData = Object.fromEntries(
        Object.entries(enrollmentData).filter(([key, value]) => value !== undefined)
      );
      
      // Save course enrollment record
      const enrollmentResult = await paymentManagementService.saveCourseEnrollmentPayment({
        ...cleanEnrollmentData,
        transactionId: transactionData.paymentId,
        paymentStatus: 'completed',
        paymentAmount: transactionData.amount,
        paymentDate: transactionData.timestamp
      });
      
      if (!enrollmentResult.success) {
        throw new Error(enrollmentResult.error || 'Failed to save course enrollment record');
      }
      toast.success(`Successfully enrolled in ${enrollmentData.courseTitle}!`);
    } catch (error) {
      throw error;
    }
  };

  const handleCoachingSubscriptionSuccess = async (transactionData) => {
    try {
      const subscriptionData = transactionData.metadata || paymentData.metadata;
      
      if (!subscriptionData) {
        return; // Don't throw, just return - let the component handle subscription update
      }
      
      // Save coaching subscription payment record
      const subscriptionResult = await paymentManagementService.saveCoachingSubscriptionPayment({
        ...subscriptionData,
        transactionId: transactionData.paymentId,
        orderId: transactionData.orderId,
        paymentStatus: 'completed',
        paymentAmount: transactionData.amount,
        paymentDate: transactionData.timestamp,
        userId: paymentData.userId || transactionData.userId,
        customerEmail: paymentData.customerEmail || transactionData.customerEmail,
        customerName: paymentData.customerName || transactionData.customerName
      });
      
      if (!subscriptionResult.success) {
        // Don't throw - let the component's onSuccess handler update the subscription
        return;
      }
      
      // Don't show toast here - let the component handle it
    } catch (error) {
      // Don't throw - let the component handle subscription update
    }
  };

  const handlePaymentFailure = (error) => {
    setPaymentStatus('failed');
    setError(typeof error === 'string' ? error : error?.message || 'Payment failed');
    setLoading(false);
    
    if (onFailure) {
      onFailure(error);
    } else {
      // Fallback error display
      toast.error(typeof error === 'string' ? error : error?.message || 'Payment failed. Please try again.');
    }
  };

  if (!isOpen) return null;

  // Ensure paymentData exists
  if (!paymentData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Payment Error</h3>
            <p className="text-gray-600 mb-4">Payment data is missing. Please try again.</p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CreditCard className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-600">Secure payment processing</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading && (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Initializing payment...</p>
            </div>
          )}

          {paymentStatus === 'success' && (
            <div className="text-center py-8">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Payment Successful!</h3>
              <p className="text-gray-600 mb-4">Your payment has been processed successfully.</p>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Close
              </button>
            </div>
          )}

          {paymentStatus === 'failed' && (
            <div className="text-center py-8">
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Payment Failed</h3>
              <p className="text-gray-600 mb-4">{error || 'Something went wrong. Please try again.'}</p>
              <div className="space-y-2">
                <button
                  onClick={initializePayment}
                  className="w-full px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={onClose}
                  className="w-full px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!loading && !paymentStatus && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Payment Details</h4>
                <div className="space-y-2 text-sm">
                  {/* Determine if GST should be shown based on payment type and coachingId */}
                  {(() => {
                    const paymentType = paymentData?.paymentType || paymentData?.type;
                    const coachingId = paymentData?.coachingId || paymentData?.metadata?.coachingId || paymentData?.enrollmentData?.coachingId;
                    
                    // Show GST breakdown for:
                    // 1. All payments except teacher_hiring
                    // 2. For batch_enrollment: only if coachingId is null (admin-created)
                    // 3. For course_enrollment: always (all courses are admin-created)
                    const shouldShowGST = paymentType !== 'teacher_hiring' && 
                      (paymentType !== 'batch_enrollment' || coachingId === null);
                    
                    if (shouldShowGST) {
                      return (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Base Amount:</span>
                            <span className="font-medium">₹{((paymentData?.amount || 0) / 1.18).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">CGST (9%):</span>
                            <span className="font-medium">₹{(((paymentData?.amount || 0) / 1.18) * 0.09).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">SGST (9%):</span>
                            <span className="font-medium">₹{(((paymentData?.amount || 0) / 1.18) * 0.09).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between border-t pt-2 mt-2">
                            <span className="text-gray-900 font-semibold">Total Amount:</span>
                            <span className="font-semibold">₹{paymentData?.amount || 0}</span>
                          </div>
                        </>
                      );
                    } else {
                      // For teacher_hiring or coaching-created batch enrollments
                      const isCoachingBatch = paymentType === 'batch_enrollment' && coachingId !== null;
                      
                      return (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-900 font-semibold">Amount to Pay:</span>
                            <span className="font-semibold text-lg">₹{paymentData?.amount || 0}</span>
                          </div>
                          <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
                            <p className="text-xs text-blue-800">
                              <strong>Note:</strong> {isCoachingBatch 
                                ? 'A 10% platform fee (inclusive of all taxes) will be deducted from the institution\'s earnings.'
                                : 'A 10% platform fee (inclusive of all taxes) will be deducted from the teacher\'s earnings.'}
                            </p>
                          </div>
                        </>
                      );
                    }
                  })()}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Description:</span>
                    <span className="font-medium">{paymentData?.description || 'Payment'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Customer:</span>
                    <span className="font-medium">{paymentData?.customerName || 'N/A'}</span>
                  </div>
                </div>
              </div>
              
              {/* Company Details */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="text-xs text-blue-900 space-y-1">
                  <p className="font-semibold">Mahstry Private Limited</p>
                  <p>GSTIN: 08AAUCM5530E1Z9</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Secure Payment</p>
                    <p>Your payment is processed securely through Razorpay. We do not store your card details.</p>
                  </div>
                </div>
              </div>

              <button
                onClick={initializePayment}
                disabled={loading}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5" />
                    <span>Pay ₹{paymentData?.amount || 0}</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
